import { Entity, ThemeSchemaResponse } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useThemeCompanies } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { SourceEvidenceSection } from '@terminal/widgets/theme-widget/tabs/theme-exposure-tab/components/source-evidence-section/SourceEvidenceSection';
import { useTranslation } from 'react-i18next';

import { StepperSection } from '../components/stepper-section/StepperSection';
import styles from './CompanyExposureTab.module.scss';

export const CompanyExposureTab = ({
  entity,
  theme,
}: {
  entity: Entity;
  theme: ThemeSchemaResponse;
}) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();
  const entityTag = params[queryKeys.entityTag];

  const { isFetching, isError, error, data } = useThemeCompanies({
    themeId: theme.id,
  });

  const companyExposureData = data?.find(e => e.company === entityTag);

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '180px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (isError || !companyExposureData) {
    throw new Error(error?.message);
  }

  const relationship = companyExposureData.relationship;
  const { rank, description } = relationship;

  const parsedThemeData = {
    theme: {
      ...theme,
      parent: theme.parent_theme,
    },
    relationship,
  };

  return (
    <div className={styles.root}>
      <StepperSection
        entity={entity}
        theme={theme}
        title={t('widget:themeOverview.drawer.entityExposureToTheme', {
          entity: entity.name_short || entity.name,
          theme: theme.name,
        })}
        activeIndex={0}
        description={description}
      />
      <div className={styles.sectionRow}>
        <div className={styles.titleBase}>
          {t('widget:themeOverview.drawer.companyExposureRank')}
        </div>
        <div className={styles.styledRankWrapper}>
          <div className={styles.rankContainer}>
            <div className={styles.rankLabel}>
              {t(
                'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.current'
              )}
            </div>
            <div className={styles.rankFlexRow}>
              <TopRankIcon rank={rank} />
            </div>
          </div>
        </div>
      </div>
      <ErrorBoundaryWrapper>
        <SourceEvidenceSection
          evidence={parsedThemeData.relationship.evidence}
          source={POSTHOG_TRACKING_SOURCES.COMPANY_TO_THEME_DRAWER}
          sourceEvidence={parsedThemeData.relationship.source_evidence}
        />
      </ErrorBoundaryWrapper>
    </div>
  );
};
